import React, { useState } from 'react';
import { useDomainConfig } from '@/hooks/useDomainConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import UserMenu from './UserMenu';
import UserProfile from './UserProfile';
import DownloadHistory from './DownloadHistory';

interface HeaderProps {
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ className = '' }) => {
  const config = useDomainConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Authentication state
  const { user, isAuthenticated } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [downloadHistoryModalOpen, setDownloadHistoryModalOpen] = useState(false);

  const { branding } = config;
  const { authentication_enabled } = config;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Function to handle smooth scrolling to sections
  const scrollToSection = (sectionId: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }

    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // We're already on the home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    // Close mobile menu if open
    setIsMobileMenuOpen(false);
  };

  // Function to handle navigation to other pages
  const handleNavigation = (path: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  // Authentication handlers
  const handleLoginClick = () => {
    setAuthMode('login');
    setAuthModalOpen(true);
  };

  const handleRegisterClick = () => {
    setAuthMode('register');
    setAuthModalOpen(true);
  };

  const handleProfileOpen = () => {
    setProfileModalOpen(true);
  };

  const handleDownloadHistoryOpen = () => {
    setDownloadHistoryModalOpen(true);
  };

  return (
    <nav className={`fixed w-full z-50 bg-white border-b border-gray-200 shadow-sm ${className}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <a
              href="/"
              onClick={(e) => handleNavigation('/', e)}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              {branding.logo.type === 'image' ? (
                <div className={`${branding.logo.icon}`} />
              ) : branding.logo.type === 'svg' ? (
                <img
                  src={branding.logo.icon}
                  alt={`${branding.name} logo`}
                  className="w-8 h-8 mr-3"
                />
              ) : (
                <span
                  className={`${branding.logo.icon} text-2xl mr-3`}
                  style={{ color: branding.colors.primary }}
                />
              )}
              {(branding.logo.type === 'fontawesome' || branding.logo.type === 'svg') && (
                <span
                  className="text-2xl font-bold"
                  style={{ color: branding.colors.primary }}
                >
                  {branding.name}
                </span>
              )}
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-6">
              <a
                href="#features"
                onClick={(e) => scrollToSection('features', e)}
                className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
              >
                Features
              </a>

              {/* Platforms Dropdown */}
              <div className="relative group">
                <button className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors flex items-center">
                  Platforms
                  <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>

                <div className="absolute left-0 mt-2 w-64 rounded-md bg-white py-2 shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                    Video Platforms
                  </div>

                  {config.supportedPlatforms
                    .filter(platform => platform.enabled && !['apple_podcasts', 'soundcloud', 'tunein', 'playerfm', 'castbox', 'xiaoyuzhou'].includes(platform.id))
                    .sort((a, b) => a.priority - b.priority)
                    .map(platform => (
                      <a
                        key={platform.id}
                        href={`/${platform.name}-downloader`}
                        onClick={(e) => handleNavigation(`/${platform.name}-downloader`, e)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors flex items-center cursor-pointer"
                      >
                        {platform.id === 'xiaohongshu' ? (
                          <div className="xiaohongshu-logo xiaohongshu-logo-dropdown" />
                        ) : platform.id === 'twitter' ? (
                          <span
                            className="x-logo text-lg mr-2"
                            style={{ color: platform.color }}
                          />
                        ) : (
                          <span
                            className={`${platform.icon} text-lg mr-2`}
                            style={{ color: platform.color }}
                          />
                        )}
                        {platform.displayName}
                      </a>
                    ))}

                  <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 border-t border-gray-100 mt-2">
                    Audio & Podcasts
                  </div>

                  {config.supportedPlatforms
                    .filter(platform => platform.enabled && ['apple_podcasts', 'soundcloud', 'tunein', 'playerfm', 'castbox', 'xiaoyuzhou'].includes(platform.id))
                    .map(platform => (
                      <a
                        key={platform.id}
                        href={`/${platform.name}-downloader`}
                        onClick={(e) => handleNavigation(`/${platform.name}-downloader`, e)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors flex items-center cursor-pointer"
                      >
                        {platform.id === 'xiaohongshu' ? (
                          <div className="xiaohongshu-logo xiaohongshu-logo-dropdown" />
                        ) : platform.id === 'twitter' ? (
                          <span
                            className="x-logo text-lg mr-2"
                            style={{ color: platform.color }}
                          />
                        ) : (
                          <span
                            className={`${platform.icon} text-lg mr-2`}
                            style={{ color: platform.color }}
                          />
                        )}
                        {platform.displayName}
                      </a>
                    ))}
                </div>
              </div>

              <a
                href="#how-it-works"
                onClick={(e) => scrollToSection('how-it-works', e)}
                className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
              >
                How It Works
              </a>

              <a
                href="/how-to-download-videos"
                onClick={(e) => handleNavigation('/how-to-download-videos', e)}
                className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
              >
                How-To Guide
              </a>

              <a
                href="/blog"
                onClick={(e) => handleNavigation('/blog', e)}
                className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
              >
                Blog
              </a>

              {/* Authentication UI */}
              {authentication_enabled && (
                isAuthenticated && user ? (
                  <UserMenu 
                    onOpenProfile={handleProfileOpen}
                    onOpenDownloadHistory={handleDownloadHistoryOpen}
                  />
                ) : (
                  <div className="flex items-center space-x-4 ml-6">
                    <button
                      onClick={handleLoginClick}
                      className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={handleRegisterClick}
                      className="px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: branding.colors.primary }}
                    >
                      Sign Up
                    </button>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              aria-label="Toggle mobile menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <a
              href="#features"
              onClick={(e) => scrollToSection('features', e)}
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Features
            </a>
            <a
              href="#how-it-works"
              onClick={(e) => scrollToSection('how-it-works', e)}
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              How It Works
            </a>
            <a
              href="/how-to-download-videos"
              onClick={(e) => handleNavigation('/how-to-download-videos', e)}
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              How-To Guide
            </a>
            <a
              href="/blog"
              onClick={(e) => handleNavigation('/blog', e)}
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Blog
            </a>

            {/* Mobile Authentication UI */}
            {authentication_enabled && (
              isAuthenticated && user ? (
                <div className="border-t border-gray-100 mt-4 pt-4">
                  <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Account
                  </div>
                  <button
                    onClick={handleProfileOpen}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                  >
                    Profile & Settings
                  </button>
                  <button
                    onClick={handleDownloadHistoryOpen}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                  >
                    Download History
                  </button>
                </div>
              ) : (
                <div className="border-t border-gray-100 mt-4 pt-4 space-y-2">
                  <button
                    onClick={handleLoginClick}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={handleRegisterClick}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white transition-opacity"
                    style={{ backgroundColor: branding.colors.primary }}
                  >
                    Sign Up
                  </button>
                </div>
              )
            )}

            {/* Mobile Platform Links */}
            <div className="border-t border-gray-100 mt-4 pt-4">
              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Popular Platforms
              </div>
              {config.supportedPlatforms
                .filter(platform => platform.enabled && ['youtube', 'tiktok', 'xiaohongshu', 'twitter', 'facebook', 'instagram'].includes(platform.id))
                .sort((a, b) => a.priority - b.priority)
                .map(platform => (
                  <a
                    key={platform.id}
                    href={`/${platform.name}-downloader`}
                    onClick={(e) => handleNavigation(`/${platform.name}-downloader`, e)}
                    className="block px-3 py-2 rounded-md text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors flex items-center cursor-pointer"
                  >
                    {platform.id === 'xiaohongshu' ? (
                      <div className="xiaohongshu-logo xiaohongshu-logo-dropdown" />
                    ) : platform.id === 'twitter' ? (
                      <span
                        className="x-logo text-lg mr-2"
                        style={{ color: platform.color }}
                      />
                    ) : (
                      <span
                        className={`${platform.icon} text-lg mr-2`}
                        style={{ color: platform.color }}
                      />
                    )}
                    {platform.displayName}
                  </a>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Authentication Modals */}
      {authentication_enabled && (
        <>
          <AuthModal
            isOpen={authModalOpen}
            onClose={() => setAuthModalOpen(false)}
            initialMode={authMode}
          />

          <UserProfile
            isOpen={profileModalOpen}
            onClose={() => setProfileModalOpen(false)}
          />

          <DownloadHistory
            isOpen={downloadHistoryModalOpen}
            onClose={() => setDownloadHistoryModalOpen(false)}
          />
        </>
      )}
    </nav>
  );
};

export default Header;
